// Modules:
import React, {
    useRef,
    useState,
    useEffect,
    useCallback,
    useContext,
} from 'react';

// Config:
import config from 'config/config';

// Utilities
import TopicConfigContext from 'utilities/topicConfigContext';
import {
    generateAnnotations,
    formatNumber,
    processCounties,
    handleMissingSuppressed,
    applyAlternativeAgeLanguage, // TODO: Remove after full dynamic chart/tables title refactor
} from 'utilities/utils';

// Material UI:
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import ButtonGroup from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Chart.js:
import { Chart } from 'chart.js';
import { Line } from 'react-chartjs-2';
import annotationPlugin from 'chartjs-plugin-annotation';

// Components:
import Selector from './Selector';

// Annotation plugin must be registered per https://www.chartjs.org/chartjs-plugin-annotation/latest/guide/.
Chart.register(annotationPlugin);

const TrendLine = ({
    counties,
    json,
    selection,
    stratification,
    setStratification,
    dataUnavailable,
    setDataUnavailable,
    styles,
    handleDropdownSelect,
    ageGroups,
    handleAgeGroupSelect,
    checkIfNull,
    setMissingSuppressedModalOpen,
    setChartInfoModalOpen,
    setShowDataUnavailable,
    // TODO: Remove after full dynamic chart/tables title refactor
    useAlternativeTitlesAndLabels,
}) => {
    // // State // //
    const [chartData, setChartData] = useState({});
    const [windowSize, setWindowSize] = useState(window.innerWidth);

    // //  Variables // //
    const MAX_MOBILE_VIEW_WIDTH = 500;

    // // Context // //
    const { tab: currentTab, topic: currentTopic } = useContext(
        TopicConfigContext
    );

    // This is reliant on all percent-based displays including the word "percent" somewhere in
    // their y-axis label field.  If this convention is not followed, the conditional logic
    // will not work.
    const PERCENT_DISPLAY = currentTab?.chartSettings?.yAxisLabel
        .toLowerCase()
        .includes('percent');

    // // Refs // //
    const chartRef = useRef();

    // // Methods // //
    const handleClick = (value) => {
        if (stratification !== value) {
            setStratification(value);
        }
    };

    const handleWindowResize = useCallback(() => {
        setWindowSize(window.innerWidth);
    }, []);

    // // Effects // //

    // Load chart if component gets new data
    useEffect(() => {
        const loadChart = () => {
            // Reset defaults if chart loads for new data:
            setChartData({});
            setDataUnavailable(false);
            if (!json || !selection) {
                return;
            }

            const {
                defaultSelection,
                baselineSettings: baseline,
                chartConfigs,
            } = currentTab.chartSettings;

            if (currentTab) {
                const getChartData = () => {
                    const data = {}; // The object chart.js will be passed
                    const xAxisField = currentTab.chartSettings.chartXAxisField; // x-axis labels are typically the time intervals for the graph
                    const dataSetNames = currentTab.chartSettings.chartDataSets;

                    // Extract labels for chart from data:
                    data.labels = json.map((item) => item[xAxisField]);

                    let datasetArray = [];

                    if (
                        stratification &&
                        // Check the set stratification exits for the current chart
                        json[0][stratification] &&
                        currentTab.chartSettings.stratifications.some(
                            (strat) => strat.field === stratification
                        )
                    ) {
                        const sampleObject = json[0];
                        const [stratifications] = sampleObject[stratification];

                        Object.keys(stratifications).forEach((stratGroup) => {
                            const dataset = chartConfigs
                                .filter(
                                    (x) =>
                                        x.datasetType ===
                                            'stratificationDatasets' &&
                                        x.stratification === stratification
                                )
                                .find((x) => x.setName === stratGroup);

                            if (dataset) {
                                dataset.data = json.map((x) =>
                                    handleMissingSuppressed(
                                        x[stratification][0][stratGroup]
                                    )
                                );

                                if (
                                    currentTab.chartSettings.ageSelectable &&
                                    stratification === 'age'
                                ) {
                                    if (ageGroups.includes(dataset.label)) {
                                        datasetArray.push(dataset);
                                    }
                                } else {
                                    datasetArray.push(dataset);
                                }
                            }
                        });

                        datasetArray
                            .sort((a, b) => {
                                if (a.order < b.order) return 1;
                                if (a.order >= b.order) return -1;
                                return 0;
                            })
                            .forEach((dataset) => {
                                dataset.backgroundColor = dataset.color;
                                dataset.borderColor = dataset.color;
                            });

                        // Second arg of `checkIfNull` is a filter key.
                        // If all values are null for the non-baseline datasets, the following method will change `dataUnavailable` to true (which will trigger the "No Data" message to display in the chart):
                        const mainDatasetsNull = checkIfNull(
                            datasetArray,
                            'baseline'
                        );
                        if (mainDatasetsNull) {
                            setDataUnavailable(true);
                        }
                    } else if (!baseline) {
                        chartConfigs
                            .filter(
                                (chartConfig) =>
                                    chartConfig.datasetType === 'mainDatasets'
                            )
                            .forEach((dataset) => {
                                dataset.data = json.map((x) =>
                                    handleMissingSuppressed(x[dataset.setName])
                                );
                                datasetArray.push(dataset);
                            });

                        datasetArray.forEach((dataset) => {
                            dataset.backgroundColor = dataset.color;
                            dataset.borderColor = dataset.color;
                        });
                    } else if (
                        selection === defaultSelection ||
                        // Condition for Nation Wide data
                        selection === config.nationWideCode ||
                        !currentTab.chartSettings.countySelectable
                    ) {
                        const baselineDatasets = chartConfigs.filter(
                            (dataset) =>
                                dataset.baseline && !dataset.stratification
                            // Omit the "All" category that is considered baseline for stratifications
                            // when in non-stratified display.
                        );

                        baselineDatasets.forEach((dataset) => {
                            dataset.data = json.map((x) =>
                                handleMissingSuppressed(x[dataset.setName])
                            );
                        });

                        // Check if all data are missing/suppressed
                        const noData = baselineDatasets.forEach((dataSet) =>
                            dataSet.data.every(
                                (dataPoint) => dataPoint === null
                            )
                        );

                        // if no data, EXIT function here:
                        if (noData) {
                            return setDataUnavailable(true);
                        }

                        baselineDatasets.forEach((dataset) => {
                            dataset.backgroundColor = dataset.color;
                            dataset.borderColor = dataset.color;
                        });

                        datasetArray =
                            baselineDatasets.length > 1
                                ? baselineDatasets
                                : [baselineDatasets[0]];
                    } else {
                        dataSetNames.forEach((setName) => {
                            const dataset = chartConfigs.find(
                                (x) =>
                                    x.datasetType === 'mainDatasets' &&
                                    x.setName === setName
                            );
                            dataset.data = json.map((x) =>
                                handleMissingSuppressed(x[setName])
                            );
                            dataset.backgroundColor = dataset.color;
                            dataset.borderColor = dataset.color;
                            datasetArray.push(dataset);
                        });

                        // Second arg of `checkIfNull` is a filter key.
                        // If all values are null for the non-baseline datasets, the following method will change `dataUnavailable` to true (which will trigger the "No Data" message to display in the chart):
                        const mainDatasetsNull = checkIfNull(
                            datasetArray,
                            'baseline'
                        );
                        if (mainDatasetsNull) {
                            setDataUnavailable(true);
                        }
                    }

                    // If all values are null, the following method will change `dataUnavailable` to true (which will trigger the "No Data" message to display in the chart):
                    const allNull = checkIfNull(datasetArray);
                    if (allNull) {
                        setDataUnavailable(true);
                    }

                    data.datasets = datasetArray;
                    return setChartData(data);
                };

                getChartData();
            }
        };

        loadChart();
    }, [
        selection,
        json,
        ageGroups,
        currentTab,
        currentTopic.topicTitle,
        checkIfNull,
        setDataUnavailable,
        stratification,
    ]);

    // Sets window width in state.  Used to set chart x-axis label rotation degree in chart.js ticks object.
    useEffect(() => {
        window.addEventListener('resize', handleWindowResize);

        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };
    }, [handleWindowResize]);

    // Only Birth Outcomes has nationwide data, removing it as a selectable option for all other topics.
    const processedCounties = processCounties(
        counties,
        currentTopic?.topicTitle
    );

    return (
        currentTab && (
            <>
                <Grid container justify="center">
                    {processedCounties.length && (
                        <Selector
                            currentTab={currentTab}
                            stratification={stratification}
                            ageGroups={ageGroups}
                            handleAgeGroupSelect={handleAgeGroupSelect}
                            processedCounties={processedCounties}
                            handleDropdownSelect={handleDropdownSelect}
                            selection={selection}
                            styles={styles}
                        />
                    )}
                </Grid>
                {dataUnavailable ? (
                    <Grid item xs={12}>
                        <div className="data-unavailable">
                            <Alert
                                sx={styles}
                                severity="info"
                                onClick={() =>
                                    setMissingSuppressedModalOpen(true)
                                }
                            >
                                Data are not available to display for{' '}
                                {counties && counties.length
                                    ? counties.find(
                                          (county) => county.code === selection
                                      ).county
                                    : 'selection.'}
                                .
                            </Alert>
                        </div>
                    </Grid>
                ) : (
                    <Grid item xs={12}>
                        <div className="chart-container">
                            {Object.keys(chartData).length ? (
                                <Line
                                    ref={chartRef}
                                    data={chartData}
                                    width={100}
                                    height={50}
                                    options={{
                                        maintainAspectRatio: false,
                                        plugins: {
                                            title: {
                                                display:
                                                    currentTab.chartSettings
                                                        .displayChartTitle,
                                                // TODO: Remove after full dynamic chart/tables title refactor
                                                text: applyAlternativeAgeLanguage(
                                                    currentTab.chartSettings
                                                        .chartTitle,
                                                    useAlternativeTitlesAndLabels
                                                ),
                                            },
                                            subtitle: {
                                                padding: {
                                                    bottom: 10,
                                                },
                                                display:
                                                    currentTab.chartSettings
                                                        .displayChartSubtitle ||
                                                    false, // TODO:  The OR statements for these two keys are to account for a real subtitle only being in place for one topic.  Once the subtitle is given for all charts, this can be removed.
                                                text:
                                                    currentTab.chartSettings
                                                        .chartSubtitle || '',
                                            },
                                            annotation: {
                                                annotations: currentTab
                                                    .chartSettings
                                                    .displayChartDiscontinuityGraphic
                                                    ? generateAnnotations(
                                                          chartData,
                                                          currentTab.chartSettings,
                                                          stratification
                                                      )
                                                    : null,
                                            },
                                            legend: {
                                                position: 'bottom',
                                            },
                                            tooltip: {
                                                mode: 'index',
                                                intersect: false,
                                                callbacks: {
                                                    label: ({
                                                        dataIndex,
                                                        dataset,
                                                    }) => {
                                                        // This solution is to differentiate between rate and non-rate data sets, as rate data sets need a trailing .0 for whole values (9 => 9.0).
                                                        // This is reliant on the 'setName' key including the word 'rate' in some capacity, but may need to be reworked to have a more consistent condition.
                                                        const isRate = dataset.setName.match(
                                                            /rate/gi
                                                        );

                                                        const value =
                                                            dataset.data[
                                                                dataIndex
                                                            ];

                                                        return isRate
                                                            ? formatNumber(
                                                                  value
                                                              )
                                                            : value.toLocaleString();
                                                    },
                                                },
                                            },
                                        },
                                        scales: {
                                            y: {
                                                grace: '20%',
                                                beginAtZero: true,
                                                ticks: {
                                                    callback: (value) =>
                                                        PERCENT_DISPLAY
                                                            ? `${value}%` &&
                                                              `${value.toLocaleString()}%`
                                                            : value &&
                                                              value.toLocaleString(),

                                                    // For charts that use a percentage, "hides" anything greater than 100
                                                    // by matching text color to white background.  This is a work around
                                                    // that allows for headroom between the data values that are the max percentage
                                                    // value (100%) and the top of the chart. See README for more detail.
                                                    color: (context) =>
                                                        PERCENT_DISPLAY &&
                                                        context.tick.value > 100
                                                            ? 'white'
                                                            : 'gray', // Otherwise return values with normal color.
                                                },
                                                title: {
                                                    display:
                                                        currentTab.chartSettings
                                                            .displayYAxisLabel,
                                                    // TODO: Remove after full dynamic chart/tables title refactor
                                                    text: applyAlternativeAgeLanguage(
                                                        currentTab.chartSettings
                                                            .yAxisLabel,
                                                        useAlternativeTitlesAndLabels
                                                    ),
                                                },
                                            },
                                            x: {
                                                ticks: {
                                                    minRotation:
                                                        // Adds a 60 degree rotation for x-axis labels.  0 means labels lay flat.
                                                        windowSize <=
                                                        MAX_MOBILE_VIEW_WIDTH
                                                            ? 60
                                                            : 0,
                                                },
                                                title: {
                                                    display:
                                                        currentTab.chartSettings
                                                            .displayXAxisLabel,
                                                    text:
                                                        currentTab.chartSettings
                                                            .xAxisLabel,
                                                },
                                            },
                                        },
                                    }}
                                />
                            ) : null}
                        </div>
                    </Grid>
                )}
                {Object.keys(chartData).length ? (
                    <div className="chart-info-modal-trigger-container">
                        <Button
                            size="small"
                            onClick={() => setChartInfoModalOpen(true)}
                        >
                            <InfoOutlinedIcon />
                        </Button>
                    </div>
                ) : null}
                {currentTab.chartSettings.stratifiable && (
                    <Grid item xs={12}>
                        <div className="chart-buttons-container">
                            <ButtonGroup
                                color="primary"
                                aria-label="text primary button group"
                                sx={styles}
                            >
                                {[
                                    <Button
                                        key={'All'}
                                        disableElevation
                                        onClick={() => handleClick()}
                                        sx={styles}
                                        variant={
                                            !stratification
                                                ? 'contained'
                                                : 'outlined'
                                        }
                                    >
                                        All
                                    </Button>,
                                    currentTab.chartSettings.stratifications.map(
                                        (strata) => (
                                            <Button
                                                key={strata.title}
                                                disableElevation
                                                onClick={() => {
                                                    if (
                                                        strata.showDataUnavailable ===
                                                        true
                                                    ) {
                                                        setShowDataUnavailable({
                                                            display: true,
                                                            stratification: true,
                                                        });
                                                    } else {
                                                        handleClick(
                                                            strata.field
                                                        );
                                                    }
                                                }}
                                                variant={
                                                    stratification ===
                                                    strata.field
                                                        ? 'contained'
                                                        : 'outlined'
                                                }
                                                sx={styles}
                                            >
                                                {strata.title}
                                            </Button>
                                        )
                                    ),
                                ]}
                            </ButtonGroup>
                        </div>
                    </Grid>
                )}
            </>
        )
    );
};

export default TrendLine;
